// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import LocalForagePlugin, { courses, syncState } from "@/plugins/LocalForagePlugin";

// TMI - content type and query definitions

import type { Course, Lesson, Section } from "@tmi/types/entities";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { useAuthStore } from "@/stores/Auth";
import { courseSeedData } from "@/services/seed-service";
import { useProductsStore } from "@/stores/Products";

// State values
const initialized= computed< boolean >( () => allCourses.value.length > 0 );
const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
const refreshing = ref< boolean >( false );

const selectedCourse = computed< Course | undefined >( () => {
    const contextBasedSelectedCourse = allCourses.value.find( ( course: Course ): boolean =>
        selectedCourseSlug.value === course.slug
    );

    if ( contextBasedSelectedCourse ) {
        return contextBasedSelectedCourse;
    }
} );

const selectedLesson = ref< Lesson | undefined >();

const defaultCourses: Course[] = [];
const allCourses = ref< Course[] >( defaultCourses );

const selectedCourseSlug = ref< string | undefined >();

export const useCoursesStore = defineStore( 'courses', () => {
    const { isAuthenticated } = storeToRefs( useAuthStore() );

    function setSelectCourseByCourseSlug( courseSlug: string ) {
        selectedCourseSlug.value = courseSlug;
    }

    function setSelectedCourseByProductSlug( productSlug: string ) {
        const product = useProductsStore().productBySlug( productSlug );
        if ( product ) {
            const contextBasedSelectedCourse = allCourses.value.find( ( course: Course ): boolean =>
                product.id === course.assoc_product_ids[ 0 ]
            );

            if ( contextBasedSelectedCourse ) {
                selectedCourseSlug.value = contextBasedSelectedCourse.slug;
            }
        }
    }

    function reset(): void {
        allCourses.value.splice( 0 );
        selectedCourseSlug.value = undefined;
        selectedLesson.value = undefined;
        loading.value = false;
        refreshing.value = false;
    }

    async function initializeAsync(): Promise< void > {
        // If not authenticated or the application context has not yet been created, return immediately
        if ( !isAuthenticated.value ) {
            logger.info( `[ Courses ].initialize() exiting - not authenticated => ${ !isAuthenticated.value } ]` );
            return;
        }

        if ( initialized.value || isLoading.value ) {
            logger.debug( '[ Courses ].initialize() exiting - already processed...' );
            return;
        }

        try {
            loading.value = true;

            const t0: number = performance.now();
            const uniqueCourses = new Set< Course >();
            if ( typeof courses === 'undefined' || typeof syncState === 'undefined' ) {
                // Wait for the driver to settle
                await LocalForagePlugin.initialize();
            }

            await courses.ready();
            const numCourses = await courses.length();
            if ( numCourses > 0 ) {
                // Clear the contents of the backing store
                allCourses.value.splice( 0 );

                // Iterate over all elements within IDB
                await courses.iterate( ( course: Course ) => {
                    uniqueCourses.add( course );
                    return;
                } );
                allCourses.value = [ ...uniqueCourses ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allCourses.value.length } courses in ${ t1 - t0 } ms` );
            } else {
                allCourses.value = courseSeedData();

                const t1: number = performance.now();
                logger.info( `Fetching & mapping ${ allCourses.value.length } course items took ${ t1 - t0 } ms` );
            }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err ) );
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    function selectLessonBySlug( slug: string ): void {
        const allLessons: Lesson[] = [];
        selectedCourse.value?.sections.map( ( section: Section ) => allLessons.push( ...section.lessons ) );
        selectedLesson.value = allLessons.find( ( lesson: Lesson ) => slug === lesson.slug );
    }

    return {
        // ACTIONS
        initializeAsync,
        reset,
        selectLessonBySlug,
        setSelectCourseByCourseSlug,
        setSelectedCourseByProductSlug,

        // GETTERS
        allCourses,
        initialized,
        isLoading,
        selectedCourse,
        selectedLesson
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useCoursesStore, import.meta.hot ) );
}
